import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { errorMessage, RegistryApi } from '../api/registryApi';
import type { RegistryMe } from '../api/registryApiTypes';
import type { RegistrySession } from './RegistrySession';
import { useBackendServiceSignIn } from './useBackendServiceSignIn';
import { usePermissionRefresh } from './usePermissionRefresh';

const SCOPE = 'openid profile email';

/**
 * Auth0 sign-in for the Registry plus the backend's answer to "may this admin use it"
 * (GET /registry/me). Redirects to Universal Login when signed out.
 */
export function useRegistryAccess(audience?: string) {
  const auth0 = useAuth0();
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently, logout } = auth0;
  const [me, setMe] = useState<RegistryMe | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [redirectFailed, setRedirectFailed] = useState(false);

  const signIn = useCallback(() => {
    setRedirectFailed(false);
    Promise.resolve(
      loginWithRedirect({
        appState: { returnTo: window.location.hash },
        authorizationParams: { audience, scope: SCOPE },
      }),
    ).catch(() => setRedirectFailed(true));
  }, [audience, loginWithRedirect]);

  const signOut = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const refused = useRef<() => void>(() => undefined);
  const api = useMemo(
    () =>
      new RegistryApi(
        () => getAccessTokenSilently({ authorizationParams: { audience, scope: SCOPE } }),
        () => refused.current(),
      ),
    [audience, getAccessTokenSilently],
  );
  usePermissionRefresh(api, refused, setMe, isAuthenticated && me !== null);

  useBackendServiceSignIn(auth0);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) signIn();
  }, [isLoading, isAuthenticated, signIn]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoadError(null);
    api
      .getMe()
      .then((result) => {
        if (!cancelled) setMe(result);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(errorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [api, isAuthenticated, attempt]);

  const session = useMemo<RegistrySession | null>(
    () => (me ? { me, api, signOut } : null),
    [me, api, signOut],
  );

  return {
    // Signed out normally means the Auth0 redirect is already under way, so keep the spinner up;
    // the sign-in card is only for when the redirect could not start.
    signedOut: !isLoading && !isAuthenticated && redirectFailed,
    loading: isLoading || !isAuthenticated || (!session && !loadError),
    loadError,
    retry: () => setAttempt((n) => n + 1),
    session,
    signIn,
    signOut,
  };
}
